import React, { useState, useEffect } from 'react';
import { useSeason } from '../../context/SeasonContext';
import {
  getPlayoffStatus,
  getPlayoffAdmin,
  createPlayoff,
  approvePlayoff,
  addPlayoffParticipant,
  removePlayoffParticipant,
  revealPlayoffPredictions,
  linkGameToPlayoff,
  calculatePlayoffResults,
  findChampionshipGame,
  analyzeFirstPlaceContenders,
  deletePlayoff,
  triggerPlayoffDeadline,
  simulateGameCompletion,
  listPostseasonGames,
  unfinalizePlayoff,
  updatePlayoffPrediction,
  deletePlayoffPrediction,
  createPlayoffPredictionForUser,
  getAllUsers
} from '../../adminApi';
import EditPredictionModal from './EditPredictionModal';
import styles from '../../styles/AdminDashboard.module.css';

const PlayoffManagementPanel = () => {
  const { effectiveSeason } = useSeason();
  const [playoff, setPlayoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [season, setSeason] = useState(effectiveSeason);

  // Contender analysis state
  const [contenderAnalysis, setContenderAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  // Game search state
  const [championshipGame, setChampionshipGame] = useState(null);
  const [searchingGame, setSearchingGame] = useState(false);
  const [postseasonGames, setPostseasonGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [showGameSelector, setShowGameSelector] = useState(false);

  // Settings state
  const [settings, setSettings] = useState({
    prediction_deadline: '',
    tiebreaker_method: 'prediction_timing',
    non_winner_ranking: 'tied'
  });

  // User selection state
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  // Prediction editing state
  const [editingPrediction, setEditingPrediction] = useState(null);
  const [creatingPrediction, setCreatingPrediction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    loadPlayoff();
    loadAvailableUsers();
  }, [season]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPlayoff = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPlayoffStatus(season);
      if (response.data.exists) {
        const adminResponse = await getPlayoffAdmin(response.data.playoff.id);
        setPlayoff(adminResponse.data);
        setSettings({
          prediction_deadline: adminResponse.data.prediction_deadline || '',
          tiebreaker_method: adminResponse.data.tiebreaker_method || 'prediction_timing',
          non_winner_ranking: adminResponse.data.non_winner_ranking || 'tied'
        });
      } else {
        setPlayoff(null);
      }
    } catch (err) {
      console.error('Error loading playoff:', err);
      setError(err.response?.data?.message || 'Failed to load playoff');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableUsers = async () => {
    try {
      const response = await getAllUsers();
      setAvailableUsers(response.data);
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleAnalyzeContenders = async () => {
    try {
      setLoadingAnalysis(true);
      setError(null);
      const response = await analyzeFirstPlaceContenders(season);
      setContenderAnalysis(response.data);
      setSuccess('✅ Contender analysis complete!');
    } catch (err) {
      console.error('Error analyzing contenders:', err);
      setError(err.response?.data?.message || 'Failed to analyze contenders');
    } finally {
      setLoadingAnalysis(false);
    }
  };

  const handleFindChampionshipGame = async () => {
    try {
      setSearchingGame(true);
      setError(null);
      const response = await findChampionshipGame(season);
      if (response.data.found) {
        setChampionshipGame(response.data.game);
        setSuccess(`✅ Found championship game: ${response.data.game.home_team_name} vs ${response.data.game.away_team_name}`);
      } else {
        setChampionshipGame(null);
        setSuccess('ℹ️ Championship game not found. Use "Browse Postseason Games" to manually select.');
      }
    } catch (err) {
      console.error('Error finding championship game:', err);
      setError(err.response?.data?.message || 'Failed to find championship game');
    } finally {
      setSearchingGame(false);
    }
  };

  const handleLoadPostseasonGames = async () => {
    try {
      setLoadingGames(true);
      setError(null);
      const response = await listPostseasonGames(season);
      setPostseasonGames(response.data || []);
      setShowGameSelector(true);
      if (response.data.length === 0) {
        setSuccess('ℹ️ No postseason games found for this season.');
      } else {
        setSuccess(`✅ Loaded ${response.data.length} postseason games.`);
      }
    } catch (err) {
      console.error('Error loading postseason games:', err);
      setError(err.response?.data?.message || 'Failed to load postseason games');
    } finally {
      setLoadingGames(false);
    }
  };

  const handleSelectGame = async (game) => {
    if (!playoff) return;
    if (!window.confirm(`Link this game to the playoff?\n\n${game.home_team_name} vs ${game.away_team_name}\nGame ID: ${game.id}\nDate: ${new Date(game.start_date).toLocaleString()}`)) {
      return;
    }
    try {
      setError(null);
      await linkGameToPlayoff(playoff.id, game.id);
      setChampionshipGame(game);
      setShowGameSelector(false);
      setSuccess(`✅ Game linked to playoff: ${game.home_team_name} vs ${game.away_team_name}`);
      await loadPlayoff();
    } catch (err) {
      console.error('Error linking game:', err);
      setError(err.response?.data?.message || 'Failed to link game');
    }
  };

  const handleCreatePlayoff = async () => {
    try {
      setError(null);
      const deadline = settings.prediction_deadline ? new Date(settings.prediction_deadline).toISOString() : null;
      await createPlayoff(season, {
        prediction_deadline: deadline,
        tiebreaker_method: settings.tiebreaker_method,
        non_winner_ranking: settings.non_winner_ranking
      });
      setSuccess('✅ Playoff created successfully!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error creating playoff:', err);
      setError(err.response?.data?.message || 'Failed to create playoff');
    }
  };

  const handleDeletePlayoff = async () => {
    if (!playoff) return;
    const confirmMessage = playoff.status === 'completed'
      ? '⚠️⚠️⚠️ WARNING: This will delete a COMPLETED playoff and all its results, rankings, and tiebreaker data! This action cannot be undone! Are you absolutely sure?'
      : '⚠️ Are you sure you want to delete this playoff? This will permanently delete all participants, predictions, and related data. This action cannot be undone!';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    try {
      setError(null);
      await deletePlayoff(playoff.id);
      setSuccess('✅ Playoff deleted successfully!');
      setPlayoff(null);
    } catch (err) {
      console.error('Error deleting playoff:', err);
      setError(err.response?.data?.message || 'Failed to delete playoff');
    }
  };

  const handleUnfinalizePlayoff = async () => {
    if (!playoff) return;
    if (!window.confirm('⚠️ This will unfinalize the completed playoff:\n\n' +
      '• Change status back to "active"\n' +
      '• Clear all score differences, ranks, and tiebreaker values\n' +
      '• Allow you to recalculate results with updated tiebreaker logic\n\n' +
      'The predictions themselves will remain unchanged. Continue?')) {
      return;
    }
    try {
      setError(null);
      await unfinalizePlayoff(playoff.id);
      setSuccess('✅ Playoff unfinalized! Results cleared. You can now recalculate.');
      await loadPlayoff();
    } catch (err) {
      console.error('Error unfinalizing playoff:', err);
      setError(err.response?.data?.message || 'Failed to unfinalize playoff');
    }
  };

  const handleApprovePlayoff = async () => {
    if (!playoff) return;
    if (!window.confirm('Are you sure you want to approve this playoff? This will make it visible to all users.')) {
      return;
    }
    try {
      setError(null);
      await approvePlayoff(playoff.id);
      setSuccess('✅ Playoff approved and activated!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error approving playoff:', err);
      setError(err.response?.data?.message || 'Failed to approve playoff');
    }
  };

  const handleAddParticipant = async (userId, autoIdentified = false) => {
    if (!playoff) return;
    try {
      setError(null);
      await addPlayoffParticipant(playoff.id, userId, autoIdentified);
      setSuccess('✅ Participant added!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error adding participant:', err);
      setError(err.response?.data?.message || 'Failed to add participant');
    }
  };

  const handleRemoveParticipant = async (userId) => {
    if (!playoff) return;
    if (!window.confirm('Are you sure you want to remove this participant?')) {
      return;
    }
    try {
      setError(null);
      await removePlayoffParticipant(playoff.id, userId);
      setSuccess('✅ Participant removed!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error removing participant:', err);
      setError(err.response?.data?.message || 'Failed to remove participant');
    }
  };

  const handleAddAllTiedUsers = async () => {
    if (!playoff || !contenderAnalysis || !contenderAnalysis.tied || contenderAnalysis.tied.length === 0) return;
    try {
      setError(null);
      for (const user of contenderAnalysis.tied) {
        await addPlayoffParticipant(playoff.id, user.id, true);
      }
      setSuccess(`✅ Added ${contenderAnalysis.tied.length} tied users as participants!`);
      await loadPlayoff();
    } catch (err) {
      console.error('Error adding tied users:', err);
      setError(err.response?.data?.message || 'Failed to add tied users');
    }
  };

  const handleLinkGame = async (gameId) => {
    if (!playoff) return;
    // If gameId is null, we're unlinking
    const gameToLink = gameId || (championshipGame ? championshipGame.id : null);
    if (!gameToLink && gameId !== null) return;
    
    try {
      setError(null);
      if (gameToLink) {
        await linkGameToPlayoff(playoff.id, gameToLink);
        setSuccess('✅ Game linked to playoff!');
      } else {
        // Unlink game (set to null)
        await linkGameToPlayoff(playoff.id, null);
        setSuccess('✅ Game link removed from playoff!');
      }
      await loadPlayoff();
    } catch (err) {
      console.error('Error linking/unlinking game:', err);
      setError(err.response?.data?.message || 'Failed to link/unlink game');
    }
  };

  const handleRevealPredictions = async () => {
    if (!playoff) return;
    if (!window.confirm('Are you sure you want to reveal predictions early? This cannot be undone.')) {
      return;
    }
    try {
      setError(null);
      await revealPlayoffPredictions(playoff.id);
      setSuccess('✅ Predictions revealed!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error revealing predictions:', err);
      setError(err.response?.data?.message || 'Failed to reveal predictions');
    }
  };

  const handleTriggerDeadline = async () => {
    if (!playoff) return;
    if (!window.confirm('⚠️ This will auto-submit 0-0 predictions for any participants who haven\'t submitted yet. Continue?')) {
      return;
    }
    try {
      setError(null);
      await triggerPlayoffDeadline(playoff.id);
      setSuccess('✅ Deadline handling completed! 0-0 predictions added for missing participants.');
      await loadPlayoff();
    } catch (err) {
      console.error('Error triggering deadline:', err);
      setError(err.response?.data?.message || 'Failed to trigger deadline handling');
    }
  };

  const handleSavePrediction = async ({ playoffId, predictionId, userId, homeScore, awayScore, skipDuplicateCheck }) => {
    try {
      setError(null);
      if (predictionId) {
        // Updating existing prediction
        await updatePlayoffPrediction(playoffId, predictionId, homeScore, awayScore, skipDuplicateCheck);
        setSuccess('✅ Prediction updated successfully!');
      } else {
        // Creating new prediction
        await createPlayoffPredictionForUser(playoffId, userId, homeScore, awayScore, skipDuplicateCheck);
        setSuccess('✅ Prediction created successfully!');
      }
      await loadPlayoff();
    } catch (err) {
      console.error('Error saving prediction:', err);
      throw err; // Re-throw to let modal handle error display
    }
  };

  const handleSimulateGameCompletion = async () => {
    if (!playoff) return;
    
    // Prompt for game scores
    const homeScore = window.prompt(`Enter final score for ${playoff.home_team_name || 'Home Team'}:`, '0');
    if (homeScore === null) return; // User cancelled
    
    const awayScore = window.prompt(`Enter final score for ${playoff.away_team_name || 'Away Team'}:`, '0');
    if (awayScore === null) return; // User cancelled
    
    const home = parseInt(homeScore);
    const away = parseInt(awayScore);
    
    if (isNaN(home) || isNaN(away) || home < 0 || away < 0) {
      setError('Please enter valid non-negative integer scores');
      return;
    }
    
    if (!window.confirm(`⚠️ This will:\n1. Set game scores to ${home}-${away}\n2. Mark game as completed\n3. Calculate and finalize playoff results\n\nContinue?`)) {
      return;
    }
    
    try {
      setError(null);
      await simulateGameCompletion(playoff.id, home, away);
      setSuccess(`✅ Game completion simulated (${home}-${away})! Playoff results calculated and finalized.`);
      await loadPlayoff();
    } catch (err) {
      console.error('Error simulating game completion:', err);
      setError(err.response?.data?.message || 'Failed to simulate game completion');
    }
  };

  const handleCalculateResults = async () => {
    if (!playoff) return;
    if (!window.confirm('⚠️ This will calculate and finalize playoff results. Make sure the game scores are already set! Continue?')) {
      return;
    }
    try {
      setError(null);
      await calculatePlayoffResults(playoff.id);
      setSuccess('✅ Results calculated and playoff finalized!');
      await loadPlayoff();
    } catch (err) {
      console.error('Error calculating results:', err);
      setError(err.response?.data?.message || 'Failed to calculate results');
    }
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (loading) {
    return <div className={styles.loading}>Loading playoff data...</div>;
  }

  return (
    <div className={styles.panel}>
      <h2>🏆 League Playoff Management</h2>

      {/* Season Selector */}
      <div className={styles.section}>
        <label>
          Season:
          <input
            type="number"
            value={season}
            onChange={(e) => setSeason(parseInt(e.target.value))}
            style={{ marginLeft: '10px', padding: '5px' }}
          />
        </label>
      </div>

      {/* Messages */}
      {error && (
        <div className={styles.errorMessage} style={{ marginBottom: '15px' }}>
          ❌ {error}
        </div>
      )}
      {success && (
        <div className={styles.successMessage} style={{ marginBottom: '15px' }}>
          {success}
        </div>
      )}

      {/* Contender Analysis Section - Always Visible */}
      <div className={styles.section}>
        <h3>First Place Contender Analysis</h3>
        <div style={{ marginBottom: '15px' }}>
          <button
            onClick={handleAnalyzeContenders}
            disabled={loadingAnalysis}
            className={styles.button}
            style={{ marginRight: '10px' }}
          >
            {loadingAnalysis ? 'Analyzing...' : 'Analyze Contenders'}
          </button>
        </div>

        {/* Display Contender Analysis */}
        {contenderAnalysis && (
          <div style={{ marginTop: '20px' }}>
            {contenderAnalysis.leaderStats && (
              <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                <strong>Current Leader Stats:</strong>
                <p>Wins: {contenderAnalysis.leaderStats.wins}, Effective Losses: {contenderAnalysis.leaderStats.effective_losses}, Net Score: {contenderAnalysis.leaderStats.net_score}</p>
              </div>
            )}

            {/* Currently Tied for First */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ color: '#4a9eff', marginBottom: '10px' }}>
                🏆 Currently Tied for First ({contenderAnalysis.tied?.length || 0})
              </h4>
              {contenderAnalysis.tied && contenderAnalysis.tied.length > 0 ? (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                    <thead>
                      <tr>
                        <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Username</th>
                        <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Wins</th>
                        <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Losses</th>
                        <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Eff. Losses</th>
                        <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contenderAnalysis.tied.map(user => (
                        <tr key={user.id}>
                          <td style={{ border: '1px solid #333', padding: '8px' }}>{user.username}</td>
                          <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.wins}</td>
                          <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.losses}</td>
                          <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.effective_losses}</td>
                          <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.accumulated_margin?.toFixed(1)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {playoff && contenderAnalysis.tied.length >= 2 && (
                    <button
                      onClick={handleAddAllTiedUsers}
                      className={styles.button}
                      style={{ marginTop: '10px' }}
                    >
                      ➕ Add These Users to Playoff
                    </button>
                  )}
                </>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No users currently tied for first</p>
              )}
            </div>

            {/* Could Tie for First */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ color: '#ff9800', marginBottom: '10px' }}>
                ⚠️ Could Still Reach Tied for First ({contenderAnalysis.couldTie?.length || 0})
              </h4>
              {contenderAnalysis.couldTie && contenderAnalysis.couldTie.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Username</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Current</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Pending</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Max Possible</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Games Behind</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contenderAnalysis.couldTie.map(user => (
                      <tr key={user.id}>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>{user.username}</td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                          {user.wins}-{user.effective_losses} (Net: {user.current_net_score})
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.pending_games}</td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                          {user.max_possible_wins}-{user.best_effective_losses} (Net: {user.best_possible_net_score})
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.games_behind}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No users can still reach tied for first</p>
              )}
            </div>

            {/* Could Surpass First */}
            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ color: '#4caf50', marginBottom: '10px' }}>
                🚀 Could Surpass and Take Sole First ({contenderAnalysis.couldSurpass?.length || 0})
              </h4>
              {contenderAnalysis.couldSurpass && contenderAnalysis.couldSurpass.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Username</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Current</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Pending</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Max Possible</th>
                      <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Could Win By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contenderAnalysis.couldSurpass.map(user => (
                      <tr key={user.id}>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>{user.username}</td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                          {user.wins}-{user.effective_losses} (Net: {user.current_net_score})
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>{user.pending_games}</td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                          {user.max_possible_wins}-{user.best_effective_losses} (Net: {user.best_possible_net_score})
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center', color: '#4caf50' }}>
                          +{user.could_win_by}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>No users can surpass first place</p>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Create Playoff Section */}
      {!playoff && (
        <div className={styles.section}>
          <h3>Create New Playoff</h3>
          <div style={{ marginBottom: '15px' }}>
            <label>
              Prediction Deadline:
              <input
                type="datetime-local"
                value={settings.prediction_deadline}
                onChange={(e) => setSettings({ ...settings, prediction_deadline: e.target.value })}
                style={{ marginLeft: '10px', padding: '5px' }}
              />
            </label>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label>
              Tiebreaker Method:
              <select
                value={settings.tiebreaker_method}
                onChange={(e) => setSettings({ ...settings, tiebreaker_method: e.target.value })}
                style={{ marginLeft: '10px', padding: '5px' }}
              >
                <option value="prediction_timing">Prediction Timing (earlier wins)</option>
                <option value="regular_season_margin">Regular Season Margin (higher wins)</option>
                <option value="margin_then_timing">Margin then Timing</option>
              </select>
            </label>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label>
              Non-Winner Ranking:
              <select
                value={settings.non_winner_ranking}
                onChange={(e) => setSettings({ ...settings, non_winner_ranking: e.target.value })}
                style={{ marginLeft: '10px', padding: '5px' }}
              >
                <option value="tied">Tied (merge pots)</option>
                <option value="sequential">Sequential (lone 2nd, lone 3rd, etc.)</option>
              </select>
            </label>
          </div>
          <button onClick={handleCreatePlayoff} className={styles.button}>
            Create Playoff
          </button>
        </div>
      )}

      {/* Existing Playoff Management */}
      {playoff && (
        <>
          {/* Playoff Status */}
          <div className={styles.section}>
            <h3>Playoff Status</h3>
            <p><strong>Status:</strong> {playoff.status}</p>
            {playoff.approved_at && <p><strong>Approved:</strong> {new Date(playoff.approved_at).toLocaleString()}</p>}
            {playoff.prediction_deadline && (
              <p><strong>Deadline:</strong> {new Date(playoff.prediction_deadline).toLocaleString()}</p>
            )}
            {playoff.game_id && (
              <p><strong>Game:</strong> {playoff.home_team_name} vs {playoff.away_team_name}</p>
            )}
          </div>


          {/* Participants Management */}
          <div className={styles.section}>
            <h3>Participants ({playoff.participants?.length || 0})</h3>
            {playoff.participants && playoff.participants.length > 0 && (
              <table style={{ width: '100%', marginBottom: '15px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Username</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Auto-Identified</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Has Prediction</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {playoff.participants.map(participant => {
                    const hasPrediction = playoff.predictions?.some(p => p.user_id === participant.user_id);
                    return (
                      <tr key={participant.id}>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>{participant.username}</td>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>
                          {participant.auto_identified ? '✅' : '❌'}
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>
                          {hasPrediction ? '✅' : '⏳'}
                        </td>
                        <td style={{ border: '1px solid #333', padding: '8px' }}>
                          {playoff.status === 'draft' && (
                            <button
                              onClick={() => handleRemoveParticipant(participant.user_id)}
                              className={styles.button}
                              style={{ padding: '5px 10px' }}
                            >
                              Remove
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {playoff.status === 'draft' && (
              <div>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  style={{ marginRight: '10px', padding: '5px' }}
                >
                  <option value="">Select user...</option>
                  {availableUsers
                    .filter(u => !playoff.participants?.some(p => p.user_id === u.id))
                    .map(user => (
                      <option key={user.id} value={user.id}>
                        {user.username}
                      </option>
                    ))}
                </select>
                <button
                  onClick={() => {
                    if (selectedUserId) {
                      handleAddParticipant(parseInt(selectedUserId), false);
                      setSelectedUserId('');
                    }
                  }}
                  className={styles.button}
                  disabled={!selectedUserId}
                >
                  Add Participant
                </button>
              </div>
            )}
          </div>

          {/* Game Selection */}
          <div className={styles.section}>
            <h3>Championship Game</h3>
            {playoff.game_id ? (
              <div>
                <p>
                  <strong>Linked Game:</strong> {playoff.home_team_name} vs {playoff.away_team_name}
                  <br />
                  <small>Game ID: {playoff.game_id}</small>
                </p>
                {playoff.status === 'draft' && (
                  <button
                    onClick={async () => {
                      if (window.confirm('Remove linked game from playoff?')) {
                        try {
                          setError(null);
                          await linkGameToPlayoff(playoff.id, null);
                          setSuccess('✅ Game link removed from playoff!');
                          await loadPlayoff();
                        } catch (err) {
                          console.error('Error unlinking game:', err);
                          setError(err.response?.data?.message || 'Failed to unlink game');
                        }
                      }
                    }}
                    className={styles.button}
                    style={{ marginTop: '10px', backgroundColor: '#dc3545', color: 'white', borderColor: '#dc3545' }}
                  >
                    Remove Game Link
                  </button>
                )}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <button
                    onClick={handleFindChampionshipGame}
                    disabled={searchingGame}
                    className={styles.button}
                    style={{ marginRight: '10px' }}
                  >
                    {searchingGame ? 'Searching...' : '🔍 Auto-Find Championship Game'}
                  </button>
                  <button
                    onClick={handleLoadPostseasonGames}
                    disabled={loadingGames}
                    className={styles.button}
                  >
                    {loadingGames ? 'Loading...' : '📋 Browse Postseason Games'}
                  </button>
                </div>
                
                {/* Auto-found game */}
                {championshipGame && (
                  <div style={{ marginTop: '15px', marginBottom: '15px', padding: '10px', backgroundColor: '#1a1a1a', borderRadius: '5px' }}>
                    <p><strong>Found:</strong> {championshipGame.home_team_name} vs {championshipGame.away_team_name}</p>
                    <p><small>Game ID: {championshipGame.id}</small></p>
                    <button
                      onClick={() => handleLinkGame(championshipGame.id)}
                      className={styles.button}
                    >
                      Link This Game
                    </button>
                  </div>
                )}

                {/* Manual game selector */}
                {showGameSelector && postseasonGames.length > 0 && (
                  <div style={{ marginTop: '15px', maxHeight: '400px', overflowY: 'auto' }}>
                    <h4 style={{ marginBottom: '10px' }}>Select Game:</h4>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                      <thead>
                        <tr>
                          <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Teams</th>
                          <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Week</th>
                          <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'left' }}>Notes</th>
                          <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {postseasonGames.map(game => (
                          <tr key={game.id}>
                            <td style={{ border: '1px solid #333', padding: '8px' }}>
                              {new Date(game.start_date).toLocaleDateString()}
                            </td>
                            <td style={{ border: '1px solid #333', padding: '8px' }}>
                              <strong>{game.home_team_name}</strong> vs <strong>{game.away_team_name}</strong>
                            </td>
                            <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                              {game.week || '-'}
                            </td>
                            <td style={{ border: '1px solid #333', padding: '8px', fontSize: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {game.notes || '-'}
                            </td>
                            <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                              <button
                                onClick={() => handleSelectGame(game)}
                                className={styles.button}
                                style={{ padding: '5px 10px', fontSize: '12px' }}
                              >
                                Select
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <button
                      onClick={() => setShowGameSelector(false)}
                      className={styles.button}
                      style={{ marginTop: '10px' }}
                    >
                      Close List
                    </button>
                  </div>
                )}
                {showGameSelector && postseasonGames.length === 0 && !loadingGames && (
                  <p style={{ color: '#888', fontStyle: 'italic', marginTop: '10px' }}>
                    No postseason games found for season {season}.
                  </p>
                )}
              </>
            )}
          </div>

          {/* Predictions */}
          <div className={styles.section}>
            <h3>Predictions</h3>
            
            {/* Show participants without predictions */}
            {playoff.participants && playoff.participants.length > 0 && (() => {
              // Check which participants don't have predictions
              const participantsWithoutPredictions = playoff.participants.filter(p => {
                const userId = p.user_id || p.id;
                return !playoff.predictions?.some(pred => pred.user_id === userId);
              });

              if (participantsWithoutPredictions.length === 0) return null;

              return (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#2a2a2a', borderRadius: '5px' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '10px', color: '#ff9800' }}>
                    Participants Without Predictions ({participantsWithoutPredictions.length})
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {participantsWithoutPredictions.map(participant => {
                      const userId = participant.user_id || participant.id;
                      return (
                        <button
                          key={userId}
                          onClick={() => {
                            setCreatingPrediction({ 
                              id: userId, 
                              username: participant.username 
                            });
                            setShowEditModal(true);
                          }}
                          className={styles.button}
                          style={{ 
                            fontSize: '13px', 
                            padding: '8px 15px',
                            backgroundColor: '#4a9eff',
                            color: 'white',
                            borderColor: '#4a9eff'
                          }}
                        >
                          ➕ Create for {participant.username}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Predictions Table */}
            {playoff.predictions && playoff.predictions.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '15px' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>User</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Home Score</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Away Score</th>
                    <th style={{ border: '1px solid #333', padding: '8px' }}>Submitted</th>
                    {playoff.status === 'completed' && (
                      <>
                        <th style={{ border: '1px solid #333', padding: '8px' }}>Score Diff</th>
                        <th style={{ border: '1px solid #333', padding: '8px' }}>Regular Season Margin</th>
                        <th style={{ border: '1px solid #333', padding: '8px' }}>Rank</th>
                        {playoff.tiebreaker_method && (
                          <th style={{ border: '1px solid #333', padding: '8px' }}>Tiebreaker</th>
                        )}
                      </>
                    )}
                    <th style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {playoff.predictions.map(pred => (
                    <tr key={pred.id}>
                      <td style={{ border: '1px solid #333', padding: '8px' }}>{pred.username}</td>
                      <td style={{ border: '1px solid #333', padding: '8px' }}>{pred.home_team_score}</td>
                      <td style={{ border: '1px solid #333', padding: '8px' }}>{pred.away_team_score}</td>
                      <td style={{ border: '1px solid #333', padding: '8px' }}>
                        {new Date(pred.submitted_at).toLocaleString()}
                      </td>
                      {playoff.status === 'completed' && (
                        <>
                          <td style={{ border: '1px solid #333', padding: '8px' }}>
                            {pred.score_difference?.toFixed(1)}
                          </td>
                          <td style={{ border: '1px solid #333', padding: '8px' }}>
                            {(pred.accumulated_margin || 0).toFixed(2)}
                          </td>
                          <td style={{ border: '1px solid #333', padding: '8px' }}>{pred.final_rank}</td>
                          {playoff.tiebreaker_method && (
                            <td style={{ border: '1px solid #333', padding: '8px', fontSize: '12px' }}>
                              {pred.tiebreaker_display || (
                                <>
                                  {playoff.tiebreaker_method === 'margin_then_timing' && (
                                    <>
                                      Margin: {(pred.tiebreaker_margin || pred.accumulated_margin || 0).toFixed(2)}
                                      <br />
                                      Submitted: {new Date(pred.tiebreaker_timing || pred.submitted_at).toLocaleString()}
                                    </>
                                  )}
                                  {playoff.tiebreaker_method === 'regular_season_margin' && (
                                    <>Margin: {(pred.tiebreaker_margin || pred.accumulated_margin || 0).toFixed(2)}</>
                                  )}
                                  {playoff.tiebreaker_method === 'prediction_timing' && (
                                    <>Submitted: {new Date(pred.tiebreaker_timing || pred.submitted_at).toLocaleString()}</>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                        </>
                      )}
                      <td style={{ border: '1px solid #333', padding: '8px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                          <button
                            onClick={() => {
                              setEditingPrediction(pred);
                              setCreatingPrediction(null);
                              setShowEditModal(true);
                            }}
                            className={styles.button}
                            style={{ 
                              padding: '5px 10px', 
                              fontSize: '12px',
                              backgroundColor: '#4a9eff',
                              color: 'white',
                              borderColor: '#4a9eff'
                            }}
                            title="Edit prediction"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete the prediction for ${pred.username} (${pred.home_team_score}-${pred.away_team_score})?${playoff.status === 'completed' ? '\n\nNote: This will reset the playoff status to active and clear calculated results.' : ''}`)) {
                                try {
                                  setError(null);
                                  await deletePlayoffPrediction(playoff.id, pred.id);
                                  setSuccess(`✅ Prediction deleted for ${pred.username}`);
                                  await loadPlayoff();
                                } catch (err) {
                                  console.error('Error deleting prediction:', err);
                                  setError(err.response?.data?.message || 'Failed to delete prediction');
                                }
                              }
                            }}
                            className={styles.button}
                            style={{ 
                              padding: '5px 10px', 
                              fontSize: '12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              borderColor: '#dc3545'
                            }}
                            title="Delete prediction"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {(!playoff.predictions || playoff.predictions.length === 0) && (
              <p style={{ color: '#888', fontStyle: 'italic' }}>No predictions submitted yet.</p>
            )}
          </div>

          {/* Actions */}
          <div className={styles.section}>
            <h3>Actions</h3>
            {playoff.status === 'completed' && (
              <>
                <button
                  onClick={handleUnfinalizePlayoff}
                  className={styles.button}
                  style={{ 
                    marginRight: '10px',
                    backgroundColor: '#17a2b8',
                    color: 'white',
                    borderColor: '#17a2b8'
                  }}
                >
                  🔄 Unfinalize & Recalculate
                </button>
                <button
                  onClick={handleDeletePlayoff}
                  className={styles.button}
                  style={{ 
                    marginRight: '10px', 
                    backgroundColor: '#dc3545', 
                    color: 'white',
                    borderColor: '#dc3545'
                  }}
                >
                  🗑️ Delete Completed Playoff
                </button>
              </>
            )}
            {(playoff.status === 'draft' || playoff.status === 'active') && (
              <button
                onClick={handleDeletePlayoff}
                className={styles.button}
                style={{ 
                  marginRight: '10px', 
                  backgroundColor: '#dc3545', 
                  color: 'white',
                  borderColor: '#dc3545'
                }}
              >
                🗑️ Delete Playoff
              </button>
            )}
            {playoff.status === 'draft' && (
              <button
                onClick={handleApprovePlayoff}
                className={styles.button}
                style={{ marginRight: '10px' }}
                disabled={!playoff.participants || playoff.participants.length < 2}
              >
                Approve & Activate Playoff
              </button>
            )}
            {playoff.status === 'active' && (
              <>
                <button
                  onClick={handleTriggerDeadline}
                  className={styles.button}
                  style={{ 
                    marginRight: '10px',
                    backgroundColor: '#ff9800',
                    color: 'white',
                    borderColor: '#ff9800'
                  }}
                >
                  ⏰ Trigger Deadline (Test)
                </button>
                <button
                  onClick={handleRevealPredictions}
                  className={styles.button}
                  style={{ marginRight: '10px' }}
                >
                  Reveal Predictions Early
                </button>
                {playoff.game_id && (
                  <>
                    <button
                      onClick={handleSimulateGameCompletion}
                      className={styles.button}
                      style={{ 
                        marginRight: '10px',
                        backgroundColor: '#4caf50',
                        color: 'white',
                        borderColor: '#4caf50'
                      }}
                    >
                      🎮 Simulate Game Completion (Test)
                    </button>
                    <button
                      onClick={handleCalculateResults}
                      className={styles.button}
                      style={{ 
                        backgroundColor: '#28a745',
                        color: 'white',
                        borderColor: '#28a745'
                      }}
                    >
                      🏆 Calculate Results (Scores Already Set)
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Audit Log */}
          {playoff.audit_log && playoff.audit_log.length > 0 && (
            <div className={styles.section}>
              <h3>Audit Log</h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {playoff.audit_log.map(log => (
                  <div key={log.id} style={{ marginBottom: '10px', padding: '8px', backgroundColor: '#1a1a1a', borderRadius: '5px' }}>
                    <strong>{log.action}</strong> by {log.admin_username} at {new Date(log.created_at).toLocaleString()}
                    {log.details && <div><small>{JSON.stringify(JSON.parse(log.details), null, 2)}</small></div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit Prediction Modal */}
      <EditPredictionModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingPrediction(null);
          setCreatingPrediction(null);
        }}
        prediction={editingPrediction}
        user={creatingPrediction}
        isCreating={!!creatingPrediction}
        playoffId={playoff?.id}
        onSave={handleSavePrediction}
      />
    </div>
  );
};

export default PlayoffManagementPanel;

